import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Siren, Hand, Shield, LogOut, Loader2 } from "lucide-react";
import { api, formatApiError, formatCount } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRoomSocket } from "@/hooks/useRoomSocket";
import { LiveBadge, VerifiedBadge, InstitutionMark } from "@/components/Brand";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { LiveStage, MediaControls } from "@/components/room/LiveStage";
import { ChatPanel } from "@/components/room/ChatPanel";
import { ReactionBar, FloatingReactions } from "@/components/room/ReactionBar";
import { ModeratorSheet } from "@/components/room/ModeratorSheet";
import { ReportDialog } from "@/components/room/ReportDialog";
import { AccessGate } from "@/components/room/AccessGate";

function StageButton({ status, send }) {
  if (status === "on_stage")
    return <button onClick={() => send("stage_leave")} className="rounded-full bg-white/10 px-4 h-11 text-sm font-bold backdrop-blur hover:bg-white/20" data-testid="btn-leave-stage"><LogOut size={15} className="inline mr-1.5" />Sair da roda</button>;
  if (status === "pending")
    return <button onClick={() => send("stage_cancel")} className="rounded-full bg-amber-500/20 px-4 h-11 text-sm font-bold text-amber-300 ring-1 ring-amber-500/40" data-testid="btn-cancel-stage-request"><Loader2 size={15} className="inline mr-1.5 animate-spin" />Na fila… cancelar</button>;
  return <button onClick={() => send("stage_request")} className="rounded-full bg-[hsl(var(--gold))] px-5 h-11 text-sm font-extrabold text-[#0B0F17] transition-transform active:scale-95" data-testid="btn-request-join-stage"><Hand size={15} className="inline mr-1.5" />Quero Participar da Roda</button>;
}

export default function LiveRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [modOpen, setModOpen] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get(`/rooms/${id}`).then((r) => setRoom(r.data)).catch(() => {
      toast.error("Sala não encontrada");
      navigate("/lives");
    });
  }, [id, navigate]);

  const canEnter = room && ["granted", "moderator"].includes(room.my_access);
  const rs = useRoomSocket(id, canEnter, {
    onKicked: (reason) => { toast.error("Você foi removido da sala por um moderador", { description: reason }); navigate("/lives"); },
    onEnded: () => { toast("A transmissão foi encerrada"); navigate("/lives"); },
    onBlocked: () => navigate("/lives"),
  });
  const [lk, setLk] = useState(null);
  const fetchToken = useCallback(() => {
    api.post(`/rooms/${id}/livekit-token`).then((r) => setLk(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { if (canEnter) fetchToken(); }, [canEnter, fetchToken]);

  if (!room) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando sala…</div>;
  if (!canEnter) return <AccessGate room={room} onUpdate={(s) => setRoom({ ...room, my_access: s })} />;

  const sendReport = async (reason) => {
    const panic = report?.panic;
    try {
      await api.post(`/rooms/${id}/report`, { reason, panic, target_user_id: report?.msg?.user_id, target_name: report?.msg?.user_name });
      toast.success(panic ? "Moderadores alertados. Você está seguro(a)." : "Denúncia enviada aos moderadores");
      setReport(null);
      if (panic) navigate("/lives");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const kick = async (m) => {
    try {
      await api.post(`/rooms/${id}/kick`, { user_id: m.user_id, reason: "Violação das regras da sala" });
      toast.success(`${m.user_name} foi expulso(a) da sala`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onStage = rs.stageStatus === "on_stage";
  const body = (
    <>
        <section className="relative flex flex-col p-2 md:p-4 h-[46vh] lg:h-auto lg:flex-1 shrink-0">
          <div className="flex-1 min-h-0">
            {lk ? <LiveStage stage={rs.stage} isMod={rs.isModerator} meId={user.id} onRemove={(uid) => rs.send("stage_remove", { user_id: uid })} />
              : <div className="grid h-full place-items-center rounded-2xl bg-card text-sm text-muted-foreground" data-testid="stage-connecting"><Loader2 className="animate-spin" /></div>}
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <StageButton status={rs.stageStatus} send={rs.send} />
              {lk && <MediaControls onStage={onStage} onNeedToken={fetchToken} />}
            </div>
            <ReactionBar onReact={(k) => rs.send("reaction", { kind: k })} />
          </div>
          <FloatingReactions items={rs.reactions} />
        </section>
        <aside className="flex-1 min-h-0 lg:flex-none lg:w-[380px] border-t lg:border-t-0 lg:border-l border-white/5 bg-card/40">
          <ChatPanel messages={rs.messages} onSend={(t) => rs.send("chat", { text: t })} isMod={rs.isModerator} meId={user.id} disabled={!rs.connected}
            onKick={kick} onReport={(m) => setReport({ msg: m, panic: false })} />
        </aside>
    </>
  );
  const bodyCls = "flex flex-1 flex-col lg:flex-row min-h-0";

  return (
    <div className="h-[100dvh] flex flex-col bg-background">
      <header className="glass z-20 flex items-center gap-3 border-b border-white/5 px-3 md:px-6 h-16 shrink-0">
        <button onClick={() => navigate("/lives")} className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10" data-testid="room-back-button"><ArrowLeft size={19} /></button>
        <InstitutionMark name={room.institution_name} logo={room.institution_logo} color={room.institution_color} size={34} />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm md:text-base font-bold" data-testid="room-title">{room.title}</h1>
          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">{room.institution_name} <VerifiedBadge size={12} /></p>
        </div>
        <LiveBadge className="hidden sm:inline-flex" />
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold font-mono-k" data-testid="room-viewer-count"><Users size={13} /> {formatCount(rs.viewers)}</span>
        {rs.isModerator && (
          <button onClick={() => setModOpen(true)} className="relative inline-flex items-center gap-1.5 rounded-full bg-violet-500/20 px-3 h-9 text-xs font-bold text-violet-200 ring-1 ring-violet-500/40" data-testid="open-moderator-panel">
            <Shield size={14} /> <span className="hidden sm:inline">Moderar</span>
            {rs.queue.length > 0 && <span className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-amber-500 text-[10px] text-black">{rs.queue.length}</span>}
          </button>
        )}
        <button onClick={() => setReport({ panic: true })} className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 h-9 text-xs font-extrabold hover:bg-red-500" data-testid="btn-panic-button">
          <Siren size={14} /> <span className="hidden sm:inline">Pânico</span>
        </button>
      </header>
      {lk ? (
        <LiveKitRoom serverUrl={lk.server_url} token={lk.token} connect audio={false} video={false} options={{ adaptiveStream: true, dynacast: true }}
          onError={(e) => toast.error(`Vídeo: ${e.message}`)} className={bodyCls} data-testid="livekit-room">
          <RoomAudioRenderer />
          {body}
        </LiveKitRoom>
      ) : <div className={bodyCls}>{body}</div>}
      {rs.isModerator && <ModeratorSheet open={modOpen} onOpenChange={setModOpen} queue={rs.queue} alerts={rs.alerts}
        onApprove={(uid) => rs.send("stage_approve", { user_id: uid })} onReject={(uid) => rs.send("stage_reject", { user_id: uid })} />}
      <ReportDialog open={!!report} onOpenChange={(o) => !o && setReport(null)} target={report?.msg} panic={report?.panic} onSubmit={sendReport} />
    </div>
  );
}
